import { CommonAPI } from 'api';
import dompurify from 'dompurify';
import SignUpPromoButton from 'features/promotion/component/signUpButton';
import { DomHandler, Parser } from 'htmlparser2';
import { t } from 'i18next';
import { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { CmsParserTypes, hasOfferDefinitionId } from 'shared/components/CmsParser';
import CountdownTimer from 'shared/components/CountDownTimer';
import { PromotionInfoLabel } from 'shared/components/PromotionInfoLabel';
import { useIsLoggedIn } from 'shared/hooks/useIsLoggedIn';
import { State } from 'shared/interface/index';

export const CmsParser = ({ data, showTimer, countdownDate, promotionId, primaryButton, isPromoButton, offerDefinitionId = 0 }: CmsParserTypes) => {
	const isLoggedIn = useIsLoggedIn();
	const userData = useSelector((state: RootStateOrAny) => state.login);
	const htmlWithoutScript = data.replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '');
	const handler = new DomHandler((error, dom) => {
		return dom;
	});
	const offers = useSelector<State, any>((state) => state.offers);
	const history = useHistory();
	const customPageElement = useRef<HTMLDivElement>(null);
	const parser = new Parser(handler);
	parser.write(data);
	const [userPromotions, setUserPromotions] = useState<any[]>();

	const cmsScript = handler.dom.filter((item) => item.type === 'script');
	const [isUserJoined, setIsUserJoined] = useState(userPromotions?.some((el: any) => el === promotionId) as boolean);
	const dispatch = useDispatch();

	async function getCustomerAndAccountData() {
		// TODO Change to a new endpoint with just the promotions and inactivePromotions https://betfan.atlassian.net/browse/BBS-451
		const response = await CommonAPI.Customer.getAccountInformation();
		if (!response.data.isError) {
			const promotions = response.data?.data?.playersProfile.promotions || [];
			const inactivePromotions = response.data?.data?.playersProfile.inactivePromotions || [];
			const mergedPromotions = [...promotions, ...inactivePromotions];
			setUserPromotions(mergedPromotions.map((el: any) => el.promotion_id));
			setIsUserJoined(mergedPromotions.some((el: any) => el.promotion_id === promotionId));
		}
	}

	const hasTargetOffer = useMemo(() => {
		if (!offerDefinitionId) return false;
		return hasOfferDefinitionId(offers, offerDefinitionId, isLoggedIn);
	}, [offers, offerDefinitionId]);

	useEffect(() => {
		if (userData?.accountDetail?.customerData?.customerId && promotionId) {
			getCustomerAndAccountData();
		}
	}, [promotionId, userData?.accountDetail?.customerData?.customerId]);

	useEffect(() => {
		const script = document.createElement('script');
		script.type = 'application/javascript';
		// @ts-expect-error typescript thinks this is react.children. I couldn't find a better solution;
		script.innerHTML = cmsScript[0]?.children[0].data || '';

		const promoScriptSection = window.document.getElementById('promo-script-section') as HTMLDivElement;

		if (Object.keys(userData.accountDetail).length !== 0) {
			sessionStorage.setItem('rawUserId', userData.accountDetail.customerData.customerId);
		} else {
			sessionStorage.removeItem('rawUserId');
		}

		if (customPageElement && customPageElement.current) {
			customPageElement.current.innerHTML = dompurify.sanitize(htmlWithoutScript || data || '', {
				ADD_ATTR: ['target'], // adding the attribute to DomPurify allowList. reference: https://github.com/cure53/DOMPurify#control-our-allow-lists-and-block-lists
			});

			const firstElement = customPageElement.current.firstElementChild;
			const pointsBoxSection = customPageElement.current.getElementsByClassName('points-box').item(0);
			const boxesSection = customPageElement.current.getElementsByClassName('boxes-wrapper').item(0);

			if (firstElement && showTimer && countdownDate) {
				const container = document.createElement('div');
				const timerComponent = document.createElement('div');

				container.appendChild(timerComponent);
				ReactDOM.render(
					<>
						<CountdownTimer deadlineDate={countdownDate} description={t('promotionFinishDescription')} />
					</>,
					container,
				);
				firstElement.insertAdjacentElement('beforebegin', container);
			}

			if (pointsBoxSection) {
				const buttonContainer = document.createElement('div');
				ReactDOM.render(
					<SignUpPromoButton
						history={history}
						promotionId={promotionId as string}
						isUserJoined={isUserJoined}
						isUserLogged={isLoggedIn}
						primaryButton={primaryButton}
						dispatch={dispatch}
						getCustomerAndAccountData={getCustomerAndAccountData}
						isPromoButton={isPromoButton}
					/>,
					buttonContainer,
				);
				pointsBoxSection.insertAdjacentElement('afterend', buttonContainer);
			}

			if (boxesSection) {
				const buttonContainer = document.createElement('div');
				ReactDOM.render(
					<SignUpPromoButton
						history={history}
						promotionId={promotionId as string}
						isUserJoined={isUserJoined}
						isUserLogged={isLoggedIn}
						primaryButton={primaryButton}
						dispatch={dispatch}
						getCustomerAndAccountData={getCustomerAndAccountData}
						isPromoButton={isPromoButton}
					/>,
					buttonContainer,
				);
				boxesSection.insertAdjacentElement('afterend', buttonContainer);
			}
		}

		promoScriptSection.appendChild(script);
		return () => {
			promoScriptSection.removeChild(script);
		};
	}, [data, userData, cmsScript, htmlWithoutScript, showTimer, countdownDate, promotionId, isUserJoined]);

	return (
		<section id="promo-script-section" className="promo-script-section flex flex-col gap-2">
			<div className="flex flex-col gap-2">
				{hasTargetOffer && <PromotionInfoLabel text={t('promotionClaimReward')} />}
				{isUserJoined && <PromotionInfoLabel text={t('promotionAlreadySignedUp')} />}
			</div>
			<div key={new Date().getTime()} ref={customPageElement} className="promotion-details" />
		</section>
	);
};
